import React, { useMemo, useCallback, useState } from 'react';
import { PageContainer, PageContent, VideoPlayer } from '../../common';
import { useCourseDetailsQuery } from '../../../gql/hooks';
import { patchAssignment } from 'app/utils/patchAssignment';
import { ScormStateUtils } from '../../../utils/scormUtils'; 
import { LoadingPopup } from '../../common/learningListController/loadingPopup/LoadingPopup';
import { Translate } from '../../../services'; 
import './styles.less';

const createTrackedVideoModel = () => ( {
    cmi: {
        comments: '',
        comments_from_lms: '',
        core: {
            credit: 'credit',
            entry: 'ab-initio',
            exit: '',
            lesson_location: '',
            lesson_mode: 'normal',
            lesson_status: 'ASSIGNED',
            restartTime: 0,
            session_time: '0000:00:00.00',
            student_id: '',
            student_name: '',
            total_time: '0000:00:00.00',
        }
    },
} );
export const VideoPage = ( props ) => {

    /**
     * Get vars from route
     */ 
    const courseId = props?.route?.params?.courseId;
    const learningObjectId = props?.route?.params?.learningObjectId;

    /**
     * Load course
     */
    const { data: courseData, error: loadingCourseError, loading: loadingCourse } = 
        useCourseDetailsQuery( courseId );
 
    /**
     * Parse data returned from useCourseDetailsQuery
     */ 
 
    const [ videoUrl, setVideoUrl ] = useState();
    const [ resumeTime, setResumeTime ] = useState();

    // If the learning object cannot be found we toggle this
    const [ learningObjectError, setLearningObjectError ] = useState( false );
 
    const scormState = useMemo( () => { 
        if ( courseData && learningObjectId ) {

            // Find the learning object
            const learningObject = courseData.learningObjects.find( i => i.id === learningObjectId );

            if ( learningObject ) {
                let result;
 
                if ( learningObject.assignment?.scormState ) {
                    result = JSON.parse( JSON.stringify( learningObject.assignment.scormState ) );
                } else {
                    result = createTrackedVideoModel();
                }
                
                setResumeTime( { time: result.cmi?.core?.restartTime || 0 } );
                setVideoUrl( learningObject.content.url );
                return result;
            } else {
                setLearningObjectError( true );
            }
        } 
    }, [ courseData, learningObjectId ] );

    /**
     * Rendering conditionals
     */
    const hasError = useMemo( () => { 
        return ( loadingCourseError || learningObjectError );
     }, [ loadingCourseError, learningObjectError ] );
      
    const isLoading = useMemo( () => {
        return loadingCourse; 
     }, [ loadingCourse ] ); 

    /**
     * Updates the progress of the assignment
     */
    const updateAssignment = useCallback( async () => {
        await patchAssignment( courseId, {
            learningObjectID: learningObjectId,
            courseID: courseId,
            scormState: scormState,
            status: scormState.cmi?.core?.lesson_status,
            timeSpent: ScormStateUtils.getTotalTime( scormState, 'seconds' ), 
            score: 0
        } );
    }, [ courseId, learningObjectId, scormState ] );
    
    /**
     * Event handlers
     */
    const [ isPlaying, setPlaying ] = useState( false );
                                                             
    const onPlay = useCallback( () => { 
        setPlaying( true );
        if ( scormState.cmi.core.lesson_status !== 'COMPLETED' ) {
            scormState.cmi.core.lesson_status = 'IN_PROGRESS';
            updateAssignment(); 
        }
    }, [ scormState, updateAssignment ] );
  
    const onPause = useCallback( () => { 
        if ( scormState.cmi.core.lesson_status !== 'COMPLETED' ) {
            setPlaying( false );
            updateAssignment();
        } 
    }, [ scormState, updateAssignment ] );

    const onComplete = useCallback( () => {
        if ( scormState.cmi.core.lesson_status !== 'COMPLETED' ) {
            scormState.cmi.core.lesson_status = 'COMPLETED';
            scormState.cmi.core.restartTime = 0;
            updateAssignment(); 
        }
    }, [ scormState, updateAssignment ] );

    const onUpdate = useCallback( ( currentTimeInSeconds ) => {
        if ( scormState.cmi.core.lesson_status !== 'COMPLETED' ) {
         
            // TODO: Reconsider doing this? We are adding our own property to the SCORM
            // model structure called `restartTime` which does not exist as a standard
            // SCORM model property. Maybe we can store this value elsewhere...
            scormState.cmi.core.restartTime = currentTimeInSeconds;

            // Attempt to update the course's total time. Note that currently, their total
            // time is just the furthest point they have reached in the video, not
            // necessarily the total amount of time a user spent with the video (perhaps
            // paused or rewatching parts). May need to update in the future.
            const previousTotalTimeInSeconds = ScormStateUtils.getTotalTime( scormState, 'seconds' );
            if ( currentTimeInSeconds > previousTotalTimeInSeconds ) {
                const newTotalStr = ScormStateUtils.formatDuration( currentTimeInSeconds * 1000, 'string' );
                scormState.cmi.core.total_time = newTotalStr; // Standard format: 0000:00:00.00
            }

            if ( isPlaying ) {
                updateAssignment();
            }
        }
    }, [ scormState, isPlaying, updateAssignment ] ); 
 
    return (
        <PageContent className="page-video">
            <PageContainer>
                { isLoading && <LoadingPopup  visible={ isLoading }/> }

                { hasError &&   
                    <h3>
                        <Translate.Text value="page.contentNotFound.description" />
                    </h3> 
                }
             
                { !isLoading && !hasError &&
                    <VideoPlayer
                        url={ videoUrl }
                        resumableAt={ resumeTime }
                        onPlay={ onPlay }
                        onPause={ onPause }
                        onUpdate={ onUpdate }
                        onComplete={ onComplete }
                    />
                }
            </PageContainer>
        </PageContent>
    );
};
